/* ============================================================
   Roles & capabilities — who sees what.

   Four roles. `owner` is the account holder (billing lives here), `admin` runs
   the business day to day, `sales` sells but never sees cost or margin, and
   `crew` is the field: today's work, nothing else.

   Everything the UI hides is driven by ONE map below, so adding a screen means
   giving it a capability rather than sprinkling role checks around. Legacy rows
   carry role "member" (the old invite default) — that maps to `admin`, which is
   what "member" behaved as before this existed, so nobody loses access on
   deploy.

   This is the UI layer. It decides what is worth rendering; it is NOT the
   security boundary. There are two below it, and as of FW-014 / L09 both
   actually exist:

     • server/src/roles.js holds the SAME map, and requireCap() gates every
       service route that mutates or returns money. Before L09 eleven routes
       checked a role (all of them `!== "owner"` on billing, texting and account
       deletion) and everything else took workspace membership as sufficient —
       so this file's claim about that layer was not true.
     • supabase/migrate_2026-09-17_roles_enforce.sql closes direct SELECT and
       write access on the money tables, which is the only thing that stops a
       restricted teammate reading the rows with the anon key regardless of
       what any JavaScript decides.

   Keep the three in step — server/src/roles.test.mjs fails if this map and the
   server's disagree about which roles hold a capability.
   ============================================================ */

export type Role = "owner" | "admin" | "sales" | "crew";

export const ROLES: { id: Role; label: string; blurb: string }[] = [
  { id: "owner", label: "Owner", blurb: "Everything, including the plan and billing." },
  { id: "admin", label: "Admin", blurb: "Runs the business — costs, margin, reports, settings. No billing." },
  { id: "sales", label: "Sales", blurb: "Quotes and customers. Sees prices, never cost or margin." },
  { id: "crew", label: "Employee", blurb: "Crew Mode and today's jobs. No money at all." },
];

/* capability → the roles that hold it */
const CAPS: Record<string, Role[]> = {
  /* money */
  "money.cost":  ["owner", "admin"],                     // cost, margin, profit, labor rates
  "money.price": ["owner", "admin", "sales"],            // customer-facing prices and totals
  pricebook:     ["owner", "admin"],                     // products & pricing, markup, tax
  /* the work */
  customers:     ["owner", "admin", "sales"],
  estimates:     ["owner", "admin", "sales"],
  jobs:          ["owner", "admin", "sales"],            // work orders + schedule
  field:         ["owner", "admin", "sales", "crew"],    // Crew Mode, job packet
  messages:      ["owner", "admin", "sales", "crew"],
  purchasing:    ["owner", "admin"],                     // inventory + POs
  /* growth */
  marketing:     ["owner", "admin", "sales"],            // widgets, sequences, print kit, lead radar, AI desk
  reports:       ["owner", "admin"],                     // business health, budget, sales team, TV
  /* admin */
  settings:      ["owner", "admin"],
  integrations:  ["owner", "admin"],                     // connect/disconnect a provider
  team:          ["owner", "admin"],
  billing:       ["owner"],
  support:       ["owner", "admin", "sales", "crew"],    // anyone may ask us for help
};

/* "member" was the old invite default and behaved as a full admin */
export function normalizeRole(role: any): Role {
  const r = String(role || "").toLowerCase();
  if (r === "owner" || r === "admin" || r === "sales" || r === "crew") return r as Role;
  if (r === "member") return "admin";
  return "owner";   // unknown / solo / offline: never lock someone out of their own workspace
}

export function can(role: any, cap: string): boolean {
  const list = CAPS[cap];
  if (!list) return true;              // an ungated capability is open
  return list.indexOf(normalizeRole(role)) > -1;
}

export function roleLabel(role: any): string {
  const r = normalizeRole(role);
  const found = ROLES.find((x) => x.id === r);
  return found ? found.label : "Owner";
}

/* the role of whoever is signed in, read off the store the app already keeps */
export function roleOf(store: any): Role {
  return normalizeRole(store && store.settings && store.settings.role);
}
export function storeCan(store: any, cap: string): boolean {
  return can(roleOf(store), cap);
}

/* where a role lands when it opens the app — crew has no dashboard worth showing */
export function homeRouteFor(role: any): string {
  return normalizeRole(role) === "crew" ? "crew" : "dashboard";
}

(window as any).VFP_ROLES = { can, roleOf, storeCan, normalizeRole, roleLabel, ROLES, homeRouteFor };
